import {
  Alert,
  AlertTitle,
  Button,
  IconButton,
  Link,
  Stack,
} from '@mui/material';
import {
  ErrorBoundary as SentryErrorBoundary,
  type FallbackRender as SentryFallbackRender,
} from '@sentry/react';
import React from 'react';
import TablerIconChevronDown from '~icons/tabler/chevron-down';
import TablerIconMoodSad from '~icons/tabler/mood-sad';
import TablerIconRefresh from '~icons/tabler/refresh';
import { useConfig } from '#/hooks';
import { useLogger } from '#/logger';
import { cn } from '#/utils/styles';

export type ChatErrorFallbackProps = Parameters<SentryFallbackRender>[0];

export function ChatMessagesErrorFallback({
  error,
  componentStack,
  resetError,
}: ChatErrorFallbackProps) {
  const [showDetails, setShowDetails] = React.useState(false);
  const err = React.useMemo(() => {
    if (error instanceof Error) {
      return error;
    }
    return new Error(String(error));
  }, [error]);

  return (
    <div className='mx-auto max-w-2xl py-8 px-4'>
      <Alert
        severity='error'
        icon={<TablerIconMoodSad />}
        action={
          <IconButton
            size='small'
            onClick={() => setShowDetails((prev) => !prev)}
          >
            <TablerIconChevronDown
              className={cn(showDetails && 'rotate-180 transition-transform')}
            />
          </IconButton>
        }
      >
        <AlertTitle>Fatal Error</AlertTitle>
        <Stack direction='column' spacing={1}>
          <span>
            Sorry we could not load the messages, please choose another Chat or
            try and refresh the page. If this issue persists, please contact
            support:{' '}
            <Link href='mailto:support@joggr.io'>support@joggr.io</Link>
          </span>
          {showDetails && (
            <div className='p-4 border border-error-dark rounded-md'>
              {err.message}
            </div>
          )}
          <Stack direction='row' spacing={1} className='pt-4'>
            <Button
              size='small'
              variant='contained'
              startIcon={<TablerIconRefresh />}
              color='error'
              onClick={resetError}
            >
              Reload
            </Button>
          </Stack>
        </Stack>
      </Alert>
    </div>
  );
}

function ChatMessagesErrorBoundary({
  children,
}: { children: React.ReactNode }) {
  const config = useConfig();
  const logger = useLogger();

  return (
    <SentryErrorBoundary
      onError={(error, info) => {
        if (config.environment !== 'production') {
          logger.error('ChatMessagesErrorBoundary', error, info);
        }
      }}
      fallback={(args) => <ChatMessagesErrorFallback {...args} />}
    >
      {children}
    </SentryErrorBoundary>
  );
}

export default ChatMessagesErrorBoundary;
